export type VehicleValuationDateDirection = "past" | "today" | "future";

const ISO_DATE_ONLY_PATTERN = /^(\d{4})-(\d{2})-(\d{2})$/;

function normalizeToLocalDay(date: Date): Date {
  return new Date(date.getFullYear(), date.getMonth(), date.getDate());
}

export function toLocalIsoDate(date: Date): string {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");

  return `${year}-${month}-${day}`;
}

export function resolveVehicleValuationDate(value?: Date | string | null): Date | null {
  if (!value) {
    return null;
  }

  if (value instanceof Date) {
    return Number.isNaN(value.getTime()) ? null : normalizeToLocalDay(value);
  }

  const isoDateMatch = ISO_DATE_ONLY_PATTERN.exec(value);

  if (isoDateMatch) {
    const [, year, month, day] = isoDateMatch;
    return new Date(Number(year), Number(month) - 1, Number(day));
  }

  const parsedDate = new Date(value);

  return Number.isNaN(parsedDate.getTime()) ? null : normalizeToLocalDay(parsedDate);
}

export function createTodayDate(now = new Date()): Date {
  return normalizeToLocalDay(now);
}

export function addYearsToLocalDate(date: Date, years: number): Date {
  return new Date(date.getFullYear() + years, date.getMonth(), date.getDate());
}

export function getVehicleValuationDateDirection(
  value?: Date | string | null,
  referenceDate: Date | string | null = new Date(),
): VehicleValuationDateDirection | null {
  const resolvedDate = resolveVehicleValuationDate(value);
  const resolvedReferenceDate = resolveVehicleValuationDate(referenceDate);

  if (!resolvedDate || !resolvedReferenceDate) {
    return null;
  }

  const selectedDay = resolvedDate.getTime();
  const referenceDay = resolvedReferenceDate.getTime();

  if (selectedDay < referenceDay) {
    return "past";
  }

  if (selectedDay > referenceDay) {
    return "future";
  }

  return "today";
}

export function getVehicleValuationTargetDateBounds(now = new Date()): {
  today: Date;
  firstFutureDate: Date;
  maxDate: Date;
} {
  const today = createTodayDate(now);

  return {
    today,
    firstFutureDate: new Date(today.getFullYear(), today.getMonth() + 1, 1),
    maxDate: addYearsToLocalDate(today, 1),
  };
}

export function isVehicleValuationTargetDateAllowed(value?: Date | string | null, now = new Date()): boolean {
  const resolvedDate = resolveVehicleValuationDate(value);

  if (!resolvedDate) {
    return false;
  }

  const { today, firstFutureDate, maxDate } = getVehicleValuationTargetDateBounds(now);

  return resolvedDate.getTime() === today.getTime() || (resolvedDate >= firstFutureDate && resolvedDate <= maxDate);
}
